import React, { useState } from 'react'
import { Pill } from '../sso-workspace/pill'
import { CopyValue } from '../sso-workspace/copy-value'
import { ConfirmDialog } from '../sso-workspace/confirm-dialog'
import { consentStatusLabel, consentStatusTone, directoryLabel, formatDate, formatDateTime, isOpen, serverMessage } from './consent-model'
import { ErrorReference } from '../error/error-reference'

// The panel shown right after "Send request" or "Generate link": what was
// recorded, where it went, when it expires, the link to copy or open, and
// Resend / Cancel request -- buttons in the confirm dialog's style (neutral
// outline; destructive outline red, the dialog's confirm solid red). `result` is the server's RequestResponse ({request, reviewURL,
// emailed, emailError}); onResend/onCancel return the next one.
export const ConsentRequestStatus = ({ result, enabled, onResend, onCancel }) => {
  const [current, setCurrent] = useState(result)
  const [error, setError] = useState(null)
  const [cause, setCause] = useState(null)
  const [notice, setNotice] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  const row = current.request
  const open = isOpen(row)

  const handleResend = async () => {
    setError(null)
    setCause(null)
    setNotice(null)
    setBusy(true)
    try {
      const next = await onResend(row.id)
      setCurrent({ ...current, ...next })
      setNotice(`Sent again to ${next.request.sentToEmail}.`)
    } catch (err) {
      setError(serverMessage(err, "The request couldn't be sent again."))
      setCause(err)
    } finally {
      setBusy(false)
    }
  }

  const handleCancel = async () => {
    setError(null)
    setCause(null)
    setBusy(true)
    try {
      const next = await onCancel(row.id)
      setCurrent({ ...current, request: next })
      setConfirming(false)
    } catch (err) {
      setError(serverMessage(err, "The request couldn't be cancelled."))
      setCause(err)
      setConfirming(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sw-inline-panel sc-status" role="status" aria-label="approval request">
      <div className="sw-inline-head">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <Pill tone={consentStatusTone(row.status)}>{consentStatusLabel(row.status)}</Pill>
          <span className="fw-semibold">{directoryLabel(row)}</span>
        </div>
      </div>

      <p className="mb-0">
        {row.sentToEmail
          ? <>Emailed to <strong>{row.sentToEmail}</strong>{row.lastSentAt ? ` on ${formatDateTime(row.lastSentAt)}` : ''}.</>
          : <>Link generated. Nobody has been emailed; copy the link and send it to your IT administrator.</>}
        {' '}
        {row.status === 'pending' && <>It expires on {formatDate(row.expiresAt)}.</>}
      </p>

      {current.emailError &&
        <p className="sw-warn mb-0" role="alert">{current.emailError}</p>}

      {open && current.reviewURL &&
        <CopyValue id={`consent-link-${row.id}`} label="Approval link" value={current.reviewURL} openHref={current.reviewURL}
          hint="Opens a page on EducationXR that explains the request, then continues to Microsoft." />}

      {notice && <p className="sw-hint mb-0">{notice}</p>}
      {error && <p className="text-danger small mb-0" role="alert">{error}</p>}
      {error && <ErrorReference error={cause} />}

      {open &&
        <div className="sw-inline-actions">
          {row.sentToEmail &&
            <button type="button" className="btn sw-btn" disabled={!enabled || busy} onClick={handleResend}>Resend</button>}
          <button type="button" className="btn sw-btn-outline-danger" disabled={!enabled || busy} onClick={() => setConfirming(true)}>Cancel request</button>
        </div>}

      <ConfirmDialog
        open={confirming}
        title="Cancel this request?"
        body={`The link for ${directoryLabel(row)} stops working. Anyone who opens it is told it was cancelled.`}
        confirmText="Cancel request"
        busy={busy}
        onConfirm={handleCancel}
        onCancel={() => setConfirming(false)}
      />
    </div>
  )
}
